import { createApi, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react"
import {
  SimulationRoom,
  Participant,
  CollaborativeChoice,
  CreateRoomRequest,
  CreateRoomResponse,
  JoinRoomRequest,
  JoinRoomResponse,
  CastVoteRequest,
  CastVoteResponse,
  ProposeChoiceRequest,
  ProposeChoiceResponse
} from "../../features/collaboration/types"

// Base URL from environment or fallback to staging
const baseUrl = process.env.REACT_APP_API_URL || "https://stageapi-simulations.meseekna.com"

// Get token from localStorage for API requests
// Supports both regular users (persist:msk:auth -> user.token) and team simulation participants (token)
const getToken = () => {
  try {
    // First, check for team simulation token (stored directly as "token")
    const teamSimToken = localStorage.getItem("token")
    if (teamSimToken) {
      // Validate it looks like a JWT (has 3 dot-separated parts)
      if (teamSimToken.split('.').length === 3) {
        return teamSimToken
      }
      console.warn('⚠️ Found "token" in localStorage but it does not appear to be a valid JWT')
    }

    // Fall back to regular user token
    const auth = JSON.parse(localStorage.getItem("persist:msk:auth") || "{}")
    const user = JSON.parse(auth.user || "{}")
    if (user.token && user.token.split('.').length === 3) {
      return user.token
    }

    return null
  } catch (e) {
    console.error("Error getting user token:", e)
    return null
  }
}

// Helper to detect CORS or network errors
export const isCorsOrNetworkError = (error: FetchBaseQueryError | undefined): boolean => {
  if (!error) return false

  // CORS errors typically manifest as 'FETCH_ERROR' with status undefined
  if (error.status === 'FETCH_ERROR') {
    return true
  }

  // Network timeout errors
  if (error.status === 'TIMEOUT_ERROR') {
    return true
  }

  return false
}

// Helper to get user-friendly error message
export const getApiErrorMessage = (error: FetchBaseQueryError | undefined): string => {
  if (!error) return 'Unknown error'

  if (isCorsOrNetworkError(error)) {
    return 'Unable to connect to server. Please check your connection and try again.'
  }

  if (typeof error.status === 'number') {
    if (error.status === 401) return 'Session expired. Please refresh the page.'
    if (error.status === 403) return 'Access denied.'
    if (error.status === 404) return 'Resource not found.'
    if (error.status >= 500) return 'Server error. Please try again later.'
  }

  return 'An error occurred. Please try again.'
}

export const collaborationApi = createApi({
  reducerPath: "collaborationApi",
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      // Get token from localStorage
      const token = getToken()
      if (token) {
        headers.set("authorization", `Bearer ${token}`)
      }
      headers.set("content-type", "application/json")
      return headers
    },
  }),
  tagTypes: ["Room", "Choice", "Participant", "Vote"],
  endpoints: (builder) => ({
    // Create a new collaborative simulation room
    createRoom: builder.mutation<CreateRoomResponse, CreateRoomRequest>({
      query: (body) => ({
        url: "/saas/collaboration/rooms/create",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Room"],
    }),

    // Find or create a room for a team simulation
    // This ensures all participants join the same room
    findOrCreateRoom: builder.mutation<CreateRoomResponse, CreateRoomRequest>({
      query: (body) => ({
        url: "/saas/collaboration/rooms/find-or-create",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Room"],
    }),

    // Get available rooms for an organization
    getRooms: builder.query<
      { status: string; code: number; data: SimulationRoom[] },
      { organizationId: number; status?: string }
    >({
      query: ({ organizationId, status }) => ({
        url: `/saas/collaboration/rooms`,
        method: "POST",
        body: { organizationId, status },
      }),
      providesTags: ["Room"],
    }),

    // Get a specific room by ID
    getRoom: builder.query<
      { status: string; code: number; data: SimulationRoom },
      string
    >({
      query: (roomId) => ({
        url: `/saas/collaboration/rooms/${roomId}`,
      }),
      providesTags: (result, error, roomId) => [{ type: "Room", id: roomId }],
    }),

    // Join a collaborative room
    joinRoom: builder.mutation<JoinRoomResponse, JoinRoomRequest>({
      query: (body) => ({
        url: `/saas/collaboration/rooms/join`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Room", "Participant"],
    }),

    // Leave a collaborative room
    leaveRoom: builder.mutation<void, { roomId: string; participantId: string }>({
      query: ({ roomId, participantId }) => ({
        url: `/saas/collaboration/rooms/${roomId}/leave`,
        method: "POST",
        body: { participantId },
      }),
      invalidatesTags: ["Room", "Participant"],
    }),

    // Get participants in a room
    getRoomParticipants: builder.query<
      { status: string; code: number; data: Participant[] },
      string
    >({
      query: (roomId) => ({
        url: `/saas/collaboration/rooms/${roomId}/participants`,
      }),
      providesTags: ["Participant"],
    }),

    // Propose a choice for voting
    proposeChoice: builder.mutation<ProposeChoiceResponse, ProposeChoiceRequest>({
      query: (body) => ({
        url: `/saas/collaboration/rooms/${body.roomId}/propose`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Room"],
    }),

    // Cast a vote on a proposed choice
    castVote: builder.mutation<CastVoteResponse, CastVoteRequest>({
      query: (body) => ({
        url: `/saas/collaboration/rooms/${body.roomId}/vote`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Vote", "Room"],
    }),

    // Get pending choices for a room
    getRoomChoices: builder.query<
      { status: string; code: number; data: CollaborativeChoice[] },
      { roomId: string; status?: string }
    >({
      query: ({ roomId, status }) => ({
        url: `/saas/collaboration/rooms/${roomId}/choices`,
        method: "POST",
        body: { status },
      }),
      providesTags: ["Choice"],
    }),

    // Start a collaborative simulation
    startCollaborativeSimulation: builder.mutation<
      { status: string; code: number; message: string },
      { roomId: string; startedBy: number }
    >({
      query: (body) => ({
        url: "/saas/collaboration/rooms/start",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { roomId }) => [
        { type: "Room", id: roomId },
      ],
    }),

    // End a collaborative simulation
    endCollaborativeSimulation: builder.mutation<
      { status: string; code: number; message: string },
      { roomId: string; endedBy: number }
    >({
      query: (body) => ({
        url: "/saas/collaboration/rooms/end",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { roomId }) => [
        { type: "Room", id: roomId },
      ],
    }),

    // Update room settings
    updateRoom: builder.mutation<
      { status: string; code: number; message: string },
      { roomId: string; updates: Partial<SimulationRoom> }
    >({
      query: ({ roomId, updates }) => ({
        url: `/saas/collaboration/rooms/${roomId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (result, error, { roomId }) => [
        { type: "Room", id: roomId },
      ],
    }),

    // Delete a room (only if not started)
    deleteRoom: builder.mutation<
      { status: string; code: number; message: string },
      { roomId: string; deletedBy: number }
    >({
      query: (body) => ({
        url: "/saas/collaboration/rooms/delete",
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["Room"],
    }),

    // Get room details
    getRoomDetails: builder.query<SimulationRoom, string>({
      query: (roomId) => `/saas/collaboration/rooms/${roomId}`,
      providesTags: ["Room"],
    }),

    // Get active rooms
    getActiveRooms: builder.query<SimulationRoom[], void>({
      query: () => "/saas/collaboration/rooms/active",
      providesTags: ["Room"],
    }),

    // Update participant status
    updateParticipantStatus: builder.mutation<void, {
      roomId: string;
      participantId: string;
      status: Participant["status"]
    }>({
      query: ({ roomId, participantId, status }) => ({
        url: `/saas/collaboration/rooms/${roomId}/participants/${participantId}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Participant", "Room"],
    }),

    // End a room
    endRoom: builder.mutation<void, { roomId: string; hostId: string }>({
      query: ({ roomId, hostId }) => ({
        url: `/saas/collaboration/rooms/${roomId}/end`,
        method: "POST",
        body: { hostId },
      }),
      invalidatesTags: ["Room"],
    }),
  }),
})

export const {
  useCreateRoomMutation,
  useFindOrCreateRoomMutation,
  useGetRoomsQuery,
  useGetRoomQuery,
  useJoinRoomMutation,
  useLeaveRoomMutation,
  useGetRoomParticipantsQuery,
  useProposeChoiceMutation,
  useCastVoteMutation,
  useGetRoomChoicesQuery,
  useStartCollaborativeSimulationMutation,
  useEndCollaborativeSimulationMutation,
  useUpdateRoomMutation,
  useDeleteRoomMutation,
  useGetRoomDetailsQuery,
  useGetActiveRoomsQuery,
  useUpdateParticipantStatusMutation,
  useEndRoomMutation,
} = collaborationApi 